import { Button, ButtonVariant } from '@amzn/stencil-react-components/button';
import { FormWrapper, TextArea } from '@amzn/stencil-react-components/form';
import { Col } from '@amzn/stencil-react-components/layout';
import { MessageBannerType } from '@amzn/stencil-react-components/message-banner';
import { ModalContent, WithModal } from '@amzn/stencil-react-components/modal';
import { Label } from '@amzn/stencil-react-components/text';
import React, { useContext, useRef, useState } from 'react';
import CreateOrUpdateQueuesBuilder from 'src/builders/CreateOrUpdateQueuesBuilder';
import GetUserDetailsBuilder from 'src/builders/GetUserDetails';
import { UserContext } from 'src/components/Authentication/Authentication';
import { SUCCESS_MSG_BANNER_AUTO_DISMISS_TIME } from 'src/constants/DisplayConstants';
import { useInlineError } from 'src/hooks/useInlineError';
import { Queue } from 'src/models/Queue';
import { QueueUserOperationType } from 'src/models/ServiceRequests/CaseAuthorityServiceRequests';
import { User } from 'src/models/User';

interface AddUsersToQueueModalProps {
  users: User[];
  queue: Queue;
  domainId: string;
  onTableReload: Function;
  setMessageBannerDetails: Function;
  flipBannerVisibility: Function;
}
const AddUsersToQueueModal = ({
  queue,
  domainId,
  onTableReload,
  setMessageBannerDetails,
  flipBannerVisibility
}: AddUsersToQueueModalProps) => {
  const userContext = useContext(UserContext).authenticatedUser;
  const [employeeIds, setEmployeeIds] = useState('');
  const inlineError = useInlineError();
  // Set when a partial success added users but the table wasn't reloaded (to
  // keep the modal mounted). We reload on dismiss so those additions show.
  const pendingReloadRef = useRef(false);

  const addUsers = async (close) => {
    inlineError.clearError();
    try {
      const queueDetails = { ...queue };
      const usersNotFound: string[] = [];
      const usersSuccessfullyAdded: string[] = [];
      const usersNotAdded: string[] = [];
      let lastAddError: unknown;
      const listOfEmployeeIds = employeeIds
        .split(',')
        .map((employeeId) => employeeId.trim())
        .filter((employeeId) => employeeId !== '');
      if (listOfEmployeeIds.length === 0) {
        inlineError.setError('Please enter at least one employee ID.');
        return;
      }
      const resolved: { employeeId: string; userId: string }[] = [];
      for (const employeeId of listOfEmployeeIds) {
        try {
          const response = await GetUserDetailsBuilder.getUserDetails(
            domainId,
            employeeId,
            undefined
          );
          resolved.push({ employeeId, userId: response.user.userId });
        } catch (error) {
          lastAddError = error;
          usersNotFound.push(employeeId);
        }
      }
      for (const { employeeId, userId } of resolved) {
        try {
          (queueDetails as any).userOperation = {
            type: QueueUserOperationType.ADD,
            userIds: [userId]
          };
          await CreateOrUpdateQueuesBuilder.editQueue(
            domainId,
            userContext.alias,
            queueDetails
          );
          // Report the operator-entered employeeId, not the internal userId.
          usersSuccessfullyAdded.push(employeeId);
        } catch (error) {
          lastAddError = error;
          usersNotAdded.push(employeeId);
        }
      }

      const summaryParts: string[] = [];
      if (usersSuccessfullyAdded.length > 0) {
        summaryParts.push(`Added: ${usersSuccessfullyAdded.join(', ')}`);
      }
      if (usersNotFound.length > 0) {
        summaryParts.push(`Not found in domain: ${usersNotFound.join(', ')}`);
      }
      if (usersNotAdded.length > 0) {
        summaryParts.push(`Failed to add: ${usersNotAdded.join(', ')}`);
      }
      const summaryText = summaryParts.join('. ') + '.';
      const totalFailed = usersNotFound.length + usersNotAdded.length;

      if (totalFailed === 0) {
        // Reset before close() so onClose doesn't reload a second time.
        pendingReloadRef.current = false;
        onTableReload(domainId);
        setMessageBannerDetails({
          messageBannerText: summaryText,
          messageBannerType: MessageBannerType.Success,
          messageBannerAutoDismiss: SUCCESS_MSG_BANNER_AUTO_DISMISS_TIME
        });
        flipBannerVisibility();
        close();
        setEmployeeIds('');
      } else {
        // Don't reload the table on failure: it flips the parent into its
        // loading state, which unmounts this modal and discards the error.
        // On partial success, keep only the outstanding IDs so a retry does
        // not re-add the users that already succeeded.
        if (usersSuccessfullyAdded.length > 0) {
          pendingReloadRef.current = true;
          setEmployeeIds([...usersNotFound, ...usersNotAdded].join(', '));
        }
        inlineError.showError(summaryText, lastAddError);
      }
    } catch (error) {
      inlineError.captureError(error);
    }
  };

  const renderModal = ({ close }) => (
    <ModalContent
      maxWidth="90vw"
      titleText={`Add Users`}
      buttons={[
        <Button
          type={'button'}
          onClick={(event) => {
            event?.preventDefault();
            void addUsers(close);
          }}
          variant={ButtonVariant.Primary}
        >
          Add Users to Queue
        </Button>
      ]}
    >
      <Col gridGap={'S300'} width={600}>
        <FormWrapper width={600}>
          <Label htmlFor="some-textarea-id">
            Enter comma-separated list of employeeIds
          </Label>
          <TextArea
            id="some-textarea-id"
            resize="vertical"
            value={employeeIds}
            onChange={(event) => {
              setEmployeeIds(event.target.value);
            }}
          />
        </FormWrapper>
        {inlineError.renderError(
          'AddUsersToQueueModal - addUsers',
          'Failed to add users to the queue.'
        )}
      </Col>
    </ModalContent>
  );
  return (
    <WithModal
      renderModal={renderModal}
      isScrollable={false}
      onClose={() => {
        if (pendingReloadRef.current) {
          pendingReloadRef.current = false;
          onTableReload(domainId);
        }
      }}
    >
      {({ open }) => (
        <Button
          variant={ButtonVariant.Tertiary}
          onClick={() => {
            inlineError.clearError();
            open();
          }}
        >
          Add Users
        </Button>
      )}
    </WithModal>
  );
};

export default AddUsersToQueueModal;
